/**
 * REGISTRO DEL SERVICE WORKER
 *
 * El archivo en sí vive en `public/service-worker.js`, fuera del empaquetado,
 * porque tiene que servirse desde la raíz: un service worker sólo controla lo
 * que cuelga de su propia carpeta.
 *
 * Se registra DESPUÉS de `load` a propósito. Registrarlo durante el arranque
 * compite por el ancho de banda con los scripts que la gente está esperando
 * para ver algo en pantalla, y no aporta nada en esa primera visita: sirve de
 * la segunda en adelante.
 *
 * En desarrollo no se registra. Un service worker sirviendo archivos viejos
 * mientras se programa es una fuente de desconciertos que no compensa.
 */

export function registrarServiceWorker() {
  if (process.env.NODE_ENV !== 'production') return;
  if (!('serviceWorker' in navigator)) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registro) => {
        // Cuando hay versión nueva, el navegador la deja "esperando" hasta
        // que se cierren todas las pestañas. Como la navegación va contra la
        // red, no hay riesgo en adelantarlo: se le pide que entre ya, y el
        // siguiente cambio de pantalla la usa.
        registro.addEventListener('updatefound', () => {
          const nuevo = registro.installing;
          if (!nuevo) return;

          nuevo.addEventListener('statechange', () => {
            if (nuevo.state === 'installed' && navigator.serviceWorker.controller) {
              nuevo.postMessage('activar-ya');
            }
          });
        });
      })
      .catch(() => {
        // Que falle no rompe nada: la aplicación funciona igual, sólo que sin
        // arranque instantáneo ni pantalla de "sin conexión".
      });
  });
}

/**
 * Da de baja el service worker y borra sus cachés.
 *
 * No se usa en el arranque; queda a mano para soporte, cuando a alguien se le
 * queda una versión pegada y hay que decirle algo por teléfono que no sea
 * «borre los datos del navegador».
 */
export async function olvidarServiceWorker() {
  if (!('serviceWorker' in navigator)) return;

  const registros = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registros.map((r) => r.unregister()));

  if (window.caches) {
    const nombres = await caches.keys();
    await Promise.all(nombres.map((n) => caches.delete(n)));
  }
}
